/**
 * Duration utility functions for converting between different time formats
 */

export interface DurationInput {
  hours: number;
  minutes: number;
}

export interface DurationDisplay {
  hours: number;
  minutes: number;
  formatted: string;
}

/**
 * Convert hours and minutes to total minutes
 * @param hours Number of hours (must be >= 0)
 * @param minutes Number of minutes (must be 0-59)
 * @returns Total minutes
 */
export const convertToMinutes = (hours: number, minutes: number): number => {
  if (hours < 0) {
    throw new Error('Hours must be greater than or equal to 0');
  }
  if (minutes < 0 || minutes > 59) {
    throw new Error('Minutes must be between 0 and 59');
  }
  return (hours * 60) + minutes;
};

/**
 * Convert total minutes to hours and minutes with formatted display
 * @param totalMinutes Total minutes to convert
 * @returns Object with hours, minutes, and formatted string
 */
export const convertFromMinutes = (totalMinutes: number | undefined | null): DurationDisplay => {
  if (!totalMinutes || totalMinutes <= 0) {
    return {
      hours: 0,
      minutes: 0,
      formatted: 'Not specified'
    };
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  let formatted = '';
  if (hours > 0) {
    formatted += `${hours} hr${hours !== 1 ? 's' : ''}`;
  }
  if (minutes > 0) {
    if (formatted) formatted += ' ';
    formatted += `${minutes} min${minutes !== 1 ? 's' : ''}`;
  }
  
  // If both are 0, show as "0 mins"
  if (!formatted) {
    formatted = '0 mins';
  }

  return {
    hours,
    minutes,
    formatted
  };
};

/**
 * Validate duration input
 * @param hours Number of hours
 * @param minutes Number of minutes
 * @returns Object with validation result and error message
 */
export const validateDuration = (hours: number | string, minutes: number | string): {
  isValid: boolean;
  error?: string;
} => {
  const hoursNum = typeof hours === 'string' ? parseInt(hours, 10) : hours;
  const minutesNum = typeof minutes === 'string' ? parseInt(minutes, 10) : minutes;

  if (isNaN(hoursNum) || hoursNum < 0) {
    return {
      isValid: false,
      error: 'Hours must be a valid number greater than or equal to 0'
    };
  }

  if (isNaN(minutesNum) || minutesNum < 0 || minutesNum > 59) {
    return {
      isValid: false,
      error: 'Minutes must be a valid number between 0 and 59'
    };
  }

  return { isValid: true };
};

/**
 * Format minutes for API responses (readable format)
 * @param totalMinutes Total minutes
 * @returns Formatted string for API responses
 */
export const formatDurationForAPI = (totalMinutes: number | undefined | null): string => {
  return convertFromMinutes(totalMinutes).formatted;
};

/**
 * Parse duration string (like "2 hrs 30 mins") back to minutes
 * Used for parsing existing data or user input
 * @param durationStr String like "2 hrs 30 mins"
 * @returns Total minutes or null if parsing fails
 */
export const parseDurationString = (durationStr: string | undefined | null): number | null => {
  if (!durationStr || durationStr === 'Not specified') {
    return null;
  }

  let totalMinutes = 0;
  
  // Match hours (1 hr, 2 hrs, etc.)
  const hoursMatch = durationStr.match(/(\d+(?:\.\d+)?)\s*hrs?/i);
  if (hoursMatch) {
    totalMinutes += Math.round(parseFloat(hoursMatch[1]) * 60);
  }
  
  // Match minutes (30 mins, 45 mins, etc.)
  const minutesMatch = durationStr.match(/(\d+)\s*mins?/i);
  if (minutesMatch) {
    totalMinutes += parseInt(minutesMatch[1], 10);
  }

  return totalMinutes > 0 ? totalMinutes : null;
};